import { _ } from "../core/polyglot/polyglot.js";
import { formatNoDecimal } from "../core/helpers/count-formatting-util.js";
import AccountGroupRowTemplate from "./reports/templates/rowTemplates/AccountGroupRowTemplate.js";
import accountRowVisibilityFactory from "./reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import AdditionFormulaNode from "./keyFigure/formula/AdditionFormulaNode.js";
import ChangeColumnTemplate from "./reports/templates/columnTemplates/ChangeColumnTemplate.js";
import DimensionSpecification from "./dimensionSpecification/DimensionSpecification.js";
import DynamicBudgetPeriodColumnTemplateSource from "./reports/templates/columnTemplates/periodColumnTemplateSource/DynamicBudgetPeriodColumnTemplateSource.js";
import FormulaRowTemplate from "./reports/templates/rowTemplates/FormulaRowTemplate.js";
import RelativePeriodTemplate from "./reports/templates/RelativePeriodTemplate.js";
import ReportDisplaySettings from "./reports/reports/ReportDisplaySettings.js";
import ReportReferenceFormulaNode from "./reports/templates/ReportReferenceFormulaNode.js";
import ReportTemplate from "./reports/templates/ReportTemplate.js";
import YearToDateColumnTemplate from "./reports/templates/columnTemplates/YearToDateColumnTemplate.js";

/**
 * The receiver encapsulates a budget and its representation as a
 * report. This is used by the budget editor which uses the same code
 * to view a report and to edit a budget.
 */
export default class BudgetReport {
	constructor() {
		// Wraps the user-chosen account row visibility into a BudgetReportAccountRowVisibility:
		this._accountRowVisibility = this._createBudgetReportVisibility();

		this._displaySettings = this._createDisplaySettings();

		this._reportTemplate = undefined;

		// Result of the interpretation of this._reportTemplate:
		this._report = undefined;

		this._dimension = undefined;
	}

	getBudget() {
		return this._budget;
	}

	setBudget(budget) {
		if (budget === this._budget) {
			return;
		}
		this._budget = budget;
		this._reportTemplate = this._createReportTemplate();
		this._report = undefined;
	}

	getBudgetId() {
		return this._budget && this._budget.getId();
	}

	getReportTemplate() {
		return this._reportTemplate;
	}

	getReport() {
		return this._report;
	}

	getChartOfAccounts() {
		return this._report.getChartOfAccounts();
	}

	setReport(report) {
		this._report = report;

		report.expandRowsUpToLevel(
			this._displaySettings.getDefaultExpansionLevel(),
		);
	}

	getName() {
		return this._budget && this._budget.getName();
	}

	getDisplaySettings() {
		return this._displaySettings;
	}

	setAccountRowVisibility(newAccountRowVisibility) {
		if (newAccountRowVisibility === this._accountRowVisibility) {
			return;
		}

		this._accountRowVisibility = this._createBudgetReportVisibility(
			newAccountRowVisibility,
		);

		this._displaySettings.setAccountVisibility(this._accountRowVisibility);
	}

	getDimension() {
		return this._dimension;
	}

	setDimension(dimension) {
		this._dimension = dimension;
	}

	getDimensionId() {
		return (this.getDimension() && this.getDimension().getId()) || "";
	}

	getFormattedData(...rest) {
		return this.getReport().getFormattedData(...rest);
	}

	addAlwaysVisibleAccountNumber(accountNumber) {
		this._report.flushRowVisibilityCache();
		this._accountRowVisibility.addAlwaysVisibleAccountNumber(accountNumber);
	}

	generateTemplateSettings() {
		const templateSettings = new Map();

		this._reportTemplate.getColumns().forEach((column) => {
			templateSettings.set(column, {
				budgetId: this._budget.getId(),
			});
		});

		return templateSettings;
	}

	generateDimensionSpecification() {
		if (!this._dimension) {
			return null;
		}

		return new DimensionSpecification({
			dimensionId: this.getDimensionId(),
		});
	}

	/**
	 * Update budget and report so they both contain `value` on the
	 * given coordinates.
	 */
	setValue({ column, row, value }) {
		this.getBudget().setAmount({
			accountNumber: row.getAccountNumber(),
			period: column.getPeriod(),
			amount: value,
			dimensionId: this.getDimensionId(),
		});

		this.getReport().setCellData({ column, row, data: value });
	}

	/**
	 * Return an array of accounts that are not yet in the budget but
	 * still part of the underlying report template.
	 */
	findUnbudgettedAccounts() {
		const unusedAccountNumbers = new Set(
			this.getBudget()
				.getUnusedAccountsByDimension(this.getDimensionId())
				.map((account) => account.getNumber()),
		);

		const allAccountsInReport = this._getAllAccounts();

		return allAccountsInReport.filter((account) =>
			unusedAccountNumbers.has(account.getNumber()),
		);
	}

	distributeYearlyAmount({ value, row }) {
		const yearlyAmount = parseInt(value);
		const rowData = this._report.getDataForRow(row);
		const monthsNumber = rowData.length - 1;
		const monthlyAmount = Math.round(yearlyAmount / monthsNumber);

		const monthlyAmounts = new Array(monthsNumber);
		monthlyAmounts.fill(monthlyAmount);

		// Adjust the amount of the first item to match the yearly amount if needed
		if (yearlyAmount !== monthlyAmount * monthsNumber) {
			const amount =
				monthlyAmount + (yearlyAmount - monthlyAmount * monthsNumber);
			monthlyAmounts[0] = amount;
		}

		const monthColumns = this._report.getColumns().slice(1);

		monthColumns.forEach((column, index) => {
			this.setValue({ value: monthlyAmounts[index], column, row });
		});
	}

	_getAllAccounts() {
		return this.getReport()
			.getAllRows()
			.filter((row) => row.isAccountRow())
			.map((accountRow) => accountRow.getAccount());
	}

	_createReportTemplate() {
		const columns = this._createColumnTemplates();
		const rows = makeRowTemplates();
		const title = _("Budget editor template");
		const displaySettings = this._displaySettings;

		return new ReportTemplate({ title, columns, rows, displaySettings });
	}

	_createColumnTemplates() {
		const yearToDateColumn = this._createYearToDateColumnTemplate();
		const monthColumns = this._createMonthColumnTemplates();

		return [yearToDateColumn, ...monthColumns];
	}

	_createYearToDateColumnTemplate() {
		return new YearToDateColumnTemplate({
			hidden: false,
			optional: true,
			periodTemplate: new RelativePeriodTemplate({
				periodType: "Year",
				shift: 0,
				shiftPeriodType: "Year",
			}),
			source: new DynamicBudgetPeriodColumnTemplateSource(),
			isEditableBudgetColumn: true,
		});
	}

	_createMonthColumnTemplates() {
		return this._budget
			.getFiscalYearMonths()
			.map((month, index) =>
				this._createMonthColumnTemplate(month, index),
			);
	}

	_createMonthColumnTemplate(month, index) {
		return new ChangeColumnTemplate({
			hidden: false,
			optional: true,
			periodTemplate: new RelativePeriodTemplate({
				periodType: "Month",
				shift: index,
				shiftPeriodType: "Month",
			}),
			source: new DynamicBudgetPeriodColumnTemplateSource(),
			isEditableBudgetColumn: true,
		});
	}

	_createDisplaySettings() {
		return new ReportDisplaySettings({
			accountVisibility: this._accountRowVisibility,
			showSummaryRows: false,
			debitPositive: true,
			formatter: formatNoDecimal,
			landscape: false,
			defaultExpansionLevel: 2,
		});
	}

	_createBudgetReportVisibility(
		next = accountRowVisibilityFactory.defaultVisibility(),
	) {
		return accountRowVisibilityFactory.budgetReportVisibility({
			next,
		});
	}
}

/**
 * Todo: Add JSDoc comment
 */
export function makeRowTemplates() {
	const incomeRow = new AccountGroupRowTemplate({
		hidden: false,
		optional: false,
		accountGroupId: "Income",
	});

	const costRow = new AccountGroupRowTemplate({
		hidden: false,
		optional: false,
		accountGroupId: "Costs",
	});

	const sumRow = new FormulaRowTemplate({
		title: _("Profit/loss"),
		visibleWhenNoData: true,
		formula: new AdditionFormulaNode({
			leftOperand: new ReportReferenceFormulaNode({
				referenceId: incomeRow.getId(),
			}),
			rightOperand: new ReportReferenceFormulaNode({
				referenceId: costRow.getId(),
			}),
		}),
	});

	return [incomeRow, costRow, sumRow];
}
